/**
 * One note: edit it continuously, publish it deliberately.
 *
 * **The split between saving and publishing is the point of this screen**, not a UI preference.
 * Saving is autosaved and cheap; publishing is what puts text in front of an agent, so it is a
 * button somebody presses. The API is built the same way — `PATCH /notes/{id}` never changes
 * status, and `POST /notes/{id}/publish` is a separate route.
 *
 * The state that costs the most to get wrong is **edited-since-published**. The note says
 * `published`, the text on screen is newer than the text an agent is being served, and if the
 * screen simply says "Published" the person believes their correction is live when it is not. So
 * that state has its own banner naming the date of the version that is actually live.
 *
 * **Kind is now a real control.** `NoteCreate`/`NoteUpdate` carry `category`, and the current
 * design export offers exactly the four the service accepts. A rule also carries a `grade`, which
 * is offered on a rule and on nothing else, because the contract refuses the combination.
 *
 * Two controls in the design are still deliberately absent, and one the design itself has dropped:
 *
 *   - **Overrides a Handbook rule.** No field exists, *and* the picker as drawn enumerated the
 *     corpus (`PY-114`, `GEN-07`, `INF-22`), which ADR-0010 forbids. It stays unbuilt even if a
 *     field is added, and the on-screen note below says so. The current export no longer draws it;
 *     the note stays anyway, because the reason it is absent has not changed and the next person
 *     to see the old mock will otherwise add it.
 *   - **Which assistant used it.** `last_returned_at` records when, never who.
 *   - **The "Delete…" button** in this rail. The current export has removed it; a row in the list
 *     still deletes, so nothing is lost.
 */

import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

import { ErrorNotice, Loading } from "../components/feedback";
import { NoteStateChip } from "../components/state-chip";
import { useToast } from "../components/toast";
import { ApiError } from "../lib/api/errors";
import * as notesApi from "../lib/api/notes-api";
import {
  hasUnpublishedChanges,
  noteState,
  type Note,
  type NoteCategory,
  type NoteGrade,
} from "../lib/api/types";
import { CATEGORIES, GRADES, asCategory, asGrade, categoryMeta } from "../lib/ui/categories";
import { absoluteDate, absoluteDateTime, byteLength, relativeTime } from "../lib/ui/format";
import { useResource } from "../lib/ui/use-resource";

/** How long the typing has to stop before a save goes out. */
const AUTOSAVE_MS = 800;

/** The three editable fields, moved around as one value so nothing can be saved half-updated. */
interface Draft {
  title: string;
  body: string;
  tags: string[];
}

function sameDraft(a: Draft, b: Draft): boolean {
  return (
    a.title === b.title &&
    a.body === b.body &&
    a.tags.length === b.tags.length &&
    a.tags.every((tag, index) => tag === b.tags[index])
  );
}

type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "failed"; message: string };

type PublishState =
  | { kind: "idle" }
  | { kind: "publishing" }
  | { kind: "published" }
  | { kind: "failed"; message: string };

export function EditorScreen() {
  const { knowledgeId } = useParams({ from: "/app/knowledge/$knowledgeId" });

  const load = useCallback(
    (signal: AbortSignal) => notesApi.getNote(knowledgeId, signal),
    [knowledgeId],
  );
  const note = useResource(load, [knowledgeId]);

  if (note.loading) return <Loading what="this note" />;
  if (note.error !== null) {
    return (
      <>
        <Link className="back-link" to="/knowledge">
          &larr; All knowledge
        </Link>
        <ErrorNotice error={note.error} onRetry={note.reload} />
      </>
    );
  }
  if (note.data === null) return null;

  return (
    <Editor
      // Remount when the id changes, so the draft buffers below never carry one note's text on to
      // another. A `useEffect` resetting them would run *after* a render that showed the wrong text.
      key={note.data.id}
      note={note.data}
      onChanged={note.set}
    />
  );
}

function Editor({ note, onChanged }: { note: Note; onChanged: (next: Note) => void }) {
  const toast = useToast();
  const [title, setTitle] = useState(note.title);
  const [body, setBody] = useState(note.body);
  const [tags, setTags] = useState<string[]>(note.tags);
  const [tagDraft, setTagDraft] = useState("");
  const [save, setSave] = useState<SaveState>({ kind: "idle" });
  const [publish, setPublish] = useState<PublishState>({ kind: "idle" });

  // What the server currently holds. State rather than a ref, so that "is there anything
  // unsaved?" is derived during render like everything else — and so a save landing re-renders the
  // save indicator instead of leaving it stale.
  const [persisted, setPersisted] = useState<Draft>({
    title: note.title,
    body: note.body,
    tags: note.tags,
  });

  const draft: Draft = { title, body, tags };
  const dirty = !sameDraft(draft, persisted);

  // The pending debounce, and the text it is going to write. Both are refs because neither is
  // rendered and both are only ever touched from an event handler or a cleanup.
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDraft = useRef<Draft | null>(null);

  // --- Autosave ----------------------------------------------------------
  //
  // Driven from the change handlers rather than from an effect over the text. An effect that
  // watches state and then sets more state is a cascading render, and — more to the point here —
  // it cannot tell an edit the person made from a re-render caused by the save that edit produced.
  const commit = useCallback(
    async (next: Draft) => {
      try {
        const saved = await notesApi.updateNote(note.id, next);
        setPersisted({ title: saved.title, body: saved.body, tags: saved.tags });
        onChanged(saved);
        setSave({ kind: "saved" });
      } catch (cause) {
        setSave({
          kind: "failed",
          message:
            cause instanceof ApiError
              ? cause.message
              : "Could not save. Your text is still here — try again.",
        });
      }
    },
    [note.id, onChanged],
  );

  const scheduleSave = useCallback(
    (next: Draft) => {
      if (pending.current !== null) clearTimeout(pending.current);
      pendingDraft.current = next;
      setSave({ kind: "saving" });
      // A new keystroke retires whatever the last publish attempt had to say. Leaving "Published.
      // Your assistant can use this now." on screen after an edit is the precise lie this screen
      // exists to prevent — the text has moved on and the agent is still being served the old
      // version, and the banner would be claiming the opposite. Caught in a browser, not by a test.
      setPublish({ kind: "idle" });
      pending.current = setTimeout(() => {
        pending.current = null;
        pendingDraft.current = null;
        void commit(next);
      }, AUTOSAVE_MS);
    },
    [commit],
  );

  /** Write anything outstanding right now. Publishing must not race the debounce. */
  const flushSave = useCallback(async () => {
    if (pending.current === null) return;
    clearTimeout(pending.current);
    pending.current = null;
    const outstanding = pendingDraft.current;
    pendingDraft.current = null;
    if (outstanding !== null) await commit(outstanding);
  }, [commit]);

  // Never leave a timer behind on a note that is no longer on screen.
  useEffect(
    () => () => {
      if (pending.current !== null) clearTimeout(pending.current);
    },
    [],
  );

  // --- Publish -----------------------------------------------------------
  async function doPublish() {
    setPublish({ kind: "publishing" });
    try {
      // Publish what is on screen, not what happened to have been saved 800ms ago. Without this,
      // pressing Publish immediately after typing publishes the previous text and the editor then
      // shows "Published" over text that is not live — the exact confusion this screen exists to
      // prevent.
      await flushSave();
      const published = await notesApi.publishNote(note.id);
      setPersisted({
        title: published.title,
        body: published.body,
        tags: published.tags,
      });
      onChanged(published);
      setPublish({ kind: "published" });
      toast("Published. Your assistant can use this now.");
    } catch (cause) {
      setPublish({
        kind: "failed",
        message:
          cause instanceof ApiError
            ? cause.message
            : "Publishing failed. Your draft is safe — retry when you are ready.",
      });
    }
  }

  /**
   * Category and grade are saved the moment they are picked, not on the autosave debounce.
   *
   * Two reasons. A `<select>` has no "stopped typing" moment to debounce against, and — the one
   * that costs something — changing the category off `rule` clears the grade *server-side*
   * (`NoteRepository.update_fields`). Writing it immediately and rendering the response is how
   * this screen learns the grade has gone, rather than displaying one the row no longer has.
   *
   * Anything outstanding on the text debounce is flushed first, so a keystroke and a category
   * change in quick succession cannot land out of order.
   */
  async function patchFiling(patch: { category?: NoteCategory; grade?: NoteGrade }) {
    setSave({ kind: "saving" });
    try {
      await flushSave();
      const updated = await notesApi.updateNote(note.id, patch);
      setPersisted({ title: updated.title, body: updated.body, tags: updated.tags });
      onChanged(updated);
      setSave({ kind: "saved" });
    } catch (cause) {
      setSave({
        kind: "failed",
        message:
          cause instanceof ApiError ? cause.message : "Could not save that — try again.",
      });
    }
  }

  const state = noteState(note);
  // Unsaved keystrokes count as something to publish. Without `dirty` the button greys out in the
  // 800ms between the last keystroke and the save landing, which reads as "there is nothing to
  // publish" at exactly the moment there is.
  const unpublished = hasUnpublishedChanges(note) || dirty;
  const publishing = publish.kind === "publishing";
  const filing = categoryMeta(note.category);

  return (
    <>
      {/* Back to the category this note is filed under, which is where the person came from. */}
      <Link className="back-link" to="/knowledge" search={{ category: note.category }}>
        &larr; {filing.plural}
      </Link>

      <div className="editor">
        <div className="editor__main">
          <input
            className="editor__title"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              scheduleSave({ ...draft, title: event.target.value });
            }}
            placeholder="A title your assistant would search for"
            aria-label="Title"
          />

          <div className="editor__meta">
            <NoteStateChip state={state} />
            <span className="editor__save" aria-live="polite">
              {save.kind === "saving" && "saving…"}
              {save.kind === "saved" && "saved just now"}
              {save.kind === "idle" && `saved · ${absoluteDateTime(note.updated_at)}`}
              {save.kind === "failed" && save.message}
            </span>
          </div>

          {/*
            The banner that matters. A published note whose text has moved on is the one case where
            what the person is reading is not what their agent is being served, and saying
            "Published" there would be actively misleading.
          */}
          {unpublished && note.status === "published" && publish.kind !== "published" && (
            <div className="banner" data-tone="warn">
              Saved as a draft. Your assistant is still using the version you published on{" "}
              {absoluteDate(note.published_at)}.
            </div>
          )}
          {publish.kind === "published" && (
            <div className="banner" data-tone="good">
              Published. Your assistant can use this now.
            </div>
          )}
          {publish.kind === "failed" && (
            <div className="banner" data-tone="bad">
              <span>
                {publish.message} Your draft is safe
                {note.published_at !== null
                  ? `, and your assistant is still using the version from ${absoluteDate(
                      note.published_at,
                    )}.`
                  : "."}
              </span>
              <button className="button-secondary" type="button" onClick={() => void doPublish()}>
                Retry publish
              </button>
            </div>
          )}

          {note.flagged && (
            <div className="flag-panel">
              <strong>Flagged — nothing is blocked.</strong> Parts of this read like instructions to
              an assistant, so it is served marked as unreviewed. If you are deliberately writing
              about prompts or injection, that is fine — the flag is information, not a judgement.
              {note.flag_reasons.length > 0 && (
                <ul className="flag-panel__reasons">
                  {note.flag_reasons.map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <textarea
            className="editor__body"
            value={body}
            onChange={(event) => {
              setBody(event.target.value);
              scheduleSave({ ...draft, body: event.target.value });
            }}
            spellCheck={false}
            aria-label="Body, markdown"
          />
          <p className="editor__footnote">
            markdown · saved automatically as you type · {byteLength(body).toLocaleString()} bytes
          </p>
        </div>

        <aside className="editor__side">
          <div>
            <button
              className="button-primary editor__publish"
              type="button"
              disabled={publishing || !unpublished}
              onClick={() => void doPublish()}
            >
              {publishing
                ? "Publishing…"
                : !unpublished
                  ? "Published"
                  : note.status === "draft"
                    ? "Publish"
                    : "Publish changes"}
            </button>
            <p className="editor__hint">
              {publishing
                ? "Doing real work — a second or two."
                : !unpublished
                  ? "Nothing new to publish. Edit it and this becomes “Publish changes”."
                  : note.status === "draft"
                    ? "Publishing is what makes this visible to your assistant. Until then it is yours alone."
                    : "Your newer text is saved but not live yet."}
            </p>
          </div>

          <div>
            <label className="field-label" htmlFor="note-category">
              Kind
            </label>
            <select
              id="note-category"
              className="field-input"
              value={note.category}
              onChange={(event) => {
                const next = asCategory(event.target.value);
                if (next === undefined || next === note.category) return;
                void patchFiling({ category: next });
              }}
            >
              {CATEGORIES.map((meta) => (
                <option key={meta.value} value={meta.value}>
                  {meta.option}
                </option>
              ))}
            </select>
          </div>

          {/*
            A grade belongs to a rule and the contract says so: `_grade_belongs_to_a_rule` refuses
            the combination outright, so this control appears on a rule and on nothing else.

            There is no "ungraded" option once one is set, and that is not an oversight either.
            `NoteUpdate` treats an absent field as unchanged and the repository writes `grade` only
            when it is not `None`, so nothing a client can send removes a grade. What does remove
            one is moving the note out of Rules, which the line below says rather than offering a
            control that would quietly do nothing.
          */}
          {note.category === "rule" && (
            <div>
              <label className="field-label" htmlFor="note-grade">
                Grade
              </label>
              <select
                id="note-grade"
                className="field-input"
                value={note.grade ?? ""}
                onChange={(event) => {
                  const next = asGrade(event.target.value);
                  if (next === undefined || next === note.grade) return;
                  void patchFiling({ grade: next });
                }}
              >
                {note.grade === null && (
                  <option value="">Not graded — pick one</option>
                )}
                {GRADES.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}
                  </option>
                ))}
              </select>
              <p className="editor__hint">
                How hard this binds your assistant. A grade can be changed but not removed — the
                API has no way to clear one. Moving this out of Rules clears it.
              </p>
            </div>
          )}

          <div>
            <p className="field-label">Labels</p>
            {tags.length > 0 && (
              <div className="tag-editor">
                {tags.map((tag) => (
                  <span className="tag-chip" key={tag}>
                    {tag}
                    <button
                      className="tag-chip__remove"
                      type="button"
                      aria-label={`Remove label ${tag}`}
                      onClick={() => {
                        const next = tags.filter((t) => t !== tag);
                        setTags(next);
                        scheduleSave({ ...draft, tags: next });
                      }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              className="field-input"
              value={tagDraft}
              onChange={(event) => {
                setTagDraft(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                const next = tagDraft.trim().toLowerCase();
                if (next === "" || tags.includes(next)) {
                  setTagDraft("");
                  return;
                }
                const nextTags = [...tags, next];
                setTags(nextTags);
                setTagDraft("");
                scheduleSave({ ...draft, tags: nextTags });
              }}
              placeholder="add label, press Enter"
              aria-label="Add a label"
            />
          </div>

          <div>
            <p className="field-label">Assistant use</p>
            <p className="editor__hint">
              {note.last_returned_at !== null
                ? `Last returned to an assistant ${relativeTime(note.last_returned_at)}.`
                : note.status === "draft"
                  ? "Not visible to assistants until you publish."
                  : "Never returned to an assistant yet. Assistants find knowledge by title — a title with the words they would search for helps."}
            </p>
            {note.last_returned_at !== null && (
              <p className="editor__footnote">
                Which assistant is not recorded — only that one asked, and when.
              </p>
            )}
          </div>

          {/*
            This note is load-bearing and must survive. An earlier design export offered a picker
            for the handbook rule a note overrides; driven in a browser it listed `PY-114`,
            `GEN-07`, `INF-22`, which is an enumeration of the corpus. ADR-0010 forbids exposing
            any surface that lets a caller learn what the handbook contains beyond what they asked
            for, and a dropdown is the friendliest possible version of that walk.

            The current export has dropped the picker. The explanation stays, because the reason it
            is absent has not changed and because the mock that drew it is still what somebody will
            be looking at when they wonder where it went.
          */}
          <div className="unbuilt-note">
            <p className="field-label">Overrides a Handbook rule</p>
            <p className="editor__hint">
              Not built, and not an oversight. An earlier design offered a menu of Handbook rule
              identifiers to override. Listing them would let anyone read off what the Handbook
              contains, which <span className="mono">ADR-0010</span> forbids — retrieval answers a
              question, it never hands over the index. This stays unbuilt even if a field for it is
              added.
            </p>
          </div>
        </aside>
      </div>
    </>
  );
}
